import {
  allPass,
  concat,
  filter,
  find,
  includes,
  map,
  not,
  pathEq,
  pathOr,
  pipe,
  prop,
  propEq,
  toUpper,
  uniq
} from "ramda";
import { __ } from "i18n-for-browser";
import { getLocalizedProperty } from "../utils";
import { PaikallisenTietovarastonAvain } from "@/enums";
import localForage from "localforage";

export async function getOpetuskieletOPHLomake(
  { maaraykset },
  { isPreviewModeOn, isReadOnly },
  locale,
  changeObjects
) {
  const _isReadOnly = isPreviewModeOn || isReadOnly;
  const ensisijaisetOpetuskieletOPH = await localForage.getItem(
    PaikallisenTietovarastonAvain.ENSISIJAISET_OPETUSKIELET_OPH
  );
  const toissijaisetOpetuskieletOPH = await localForage.getItem(
    PaikallisenTietovarastonAvain.TOISSIJAISET_OPETUSKIELET_OPH
  );
  const lisatiedot = await localForage.getItem(
    PaikallisenTietovarastonAvain.LISATIEDOT
  );

  const lisatietomaarays = find(propEq("koodisto", "lisatietoja"), maaraykset);

  const lisatiedotObj = find(
    pathEq(["koodisto", "koodistoUri"], "lisatietoja"),
    lisatiedot || []
  );

  const valitutEnsisijaisetKoodiarvot = pipe(
    find(obj => obj.anchor?.endsWith(".ensisijaiset")),
    pathOr([], ["properties", "value"]),
    map(prop("value"))
  )(changeObjects);

  const valitutToissijaisetKoodiarvot = pipe(
    find(obj => obj.anchor?.endsWith(".ensisijaiset")),
    pathOr([], ["properties", "value"]),
    map(prop("value"))
  )(changeObjects);

  const valitutKoodiarvot = uniq(
    concat(valitutEnsisijaisetKoodiarvot, valitutToissijaisetKoodiarvot)
  );

  const valittavanaOlevatEnsisisijaisetOpetuskielet = filter(
    kieli => not(includes(kieli.koodiarvo, valitutKoodiarvot)),
    ensisijaisetOpetuskieletOPH || []
  );

  const valittavanaOlevatToissisijaisetOpetuskielet = filter(
    kieli => not(includes(kieli.koodiarvo, valitutKoodiarvot)),
    toissijaisetOpetuskieletOPH || []
  );

  const ensisijaisetAnchor = "ensisijaiset";
  const toissijaisetAnchor = "toissijaiset";

  const ensisijaisetOpetuskieletOptions = map(kieli => {
    return {
      label: getLocalizedProperty(kieli.metadata, locale, "nimi"),
      value: kieli.koodiarvo
    };
  }, valittavanaOlevatEnsisisijaisetOpetuskielet);

  const toissijaisetOpetuskieletOptions = map(kieli => {
    return {
      label: getLocalizedProperty(kieli.metadata, locale, "nimi"),
      value: kieli.koodiarvo
    };
  }, valittavanaOlevatToissisijaisetOpetuskielet);

  const ensisijainenOpetuskieliEnglanti = find(
    allPass([
      pathEq(["koodiarvo"], "EN"),
      pathEq(["koodisto", "koodistoUri"], "kielikoodistoopetushallinto")
    ])
  )(ensisijaisetOpetuskieletOPH);

  const maaraysEnglanti = find(
    maarays =>
      maarays.koodiarvo?.toLowerCase() === ensisijainenOpetuskieliEnglanti.koodiarvo.toLowerCase() &&
      maarays.koodisto === "kielikoodistoopetushallinto" &&
      maarays.meta.valikko === "ensisijaiset"
  )(maaraykset);

  const isEnglantiSelected =
    (maaraysEnglanti?.koodiarvo === "en" && valitutEnsisijaisetKoodiarvot?.length === 0) ||
    (valitutEnsisijaisetKoodiarvot?.includes("EN") ?? false);

  return [
    {
      anchor: "opetuskieli",
      title: "Opetuskieli",
      components: [
        {
          anchor: ensisijaisetAnchor,
          name: "Autocomplete",
          short: true,
          properties: {
            forChangeObject: {
              valikko: ensisijaisetAnchor
            },
            isPreviewModeOn,
            isReadOnly: _isReadOnly,
            options: ensisijaisetOpetuskieletOptions,
            title: __("common.valitseYksiTaiUseampi"),
            value: map(maarays => {
              const maarayksenKoodiarvoUpper = toUpper(maarays.koodiarvo);
              return find(
                propEq("value", maarayksenKoodiarvoUpper),
                ensisijaisetOpetuskieletOptions
              );
            }, filter(pathEq(["meta", "valikko"], ensisijaisetAnchor), maaraykset))
          }
        }
      ]
    },
    (isEnglantiSelected) ?
    {
      anchor: "opetuskieli-englanti",
      styleClasses: ["mt-4"],
      categories: [
        {
          anchor: "ensisijaiset",
          components: [
            {
              anchor: "opiskelijamaara-info",
              name: "StatusTextRow",
              properties: {
                title: "Määritä opiskelijamäärän enimmäismäärä:"
              }
            }
          ],
          layout: { indentation: "none", margins: { top: "small", bottom: "small" } }
        },

        {
          anchor: "ensisijaiset",
          components: [
            {
              anchor: "lkm",
              name: "Input",
              properties: {
                forChangeObject: {
                  lkm: ensisijaisetAnchor,
                  koodiarvo: ensisijainenOpetuskieliEnglanti.koodiarvo
                },
                fullWidth: true,
                _isReadOnly,
                label: __("education.opiskelijamaara"),
                type: "number",
                value: maaraysEnglanti?.arvo || "0"
              }
            }
          ],
          layout: { indentation: "none" }
        },
        {
          anchor: "ensisijaiset",
          components: [
            {
              anchor: "kuvaus",
              name: "TextBox",
              properties: {
                forChangeObject: {
                  kuvaus: ensisijaisetAnchor,
                  koodiarvo: ensisijainenOpetuskieliEnglanti.koodiarvo
                },
                isPreviewModeOn,
                isReadOnly: _isReadOnly,
                placeholder: __("common.kuvausPlaceholder"),
                title: __("common.kuvaus"),
                value: maaraysEnglanti?.meta?.kuvaus || "englanti"
              }
            }
          ],
          layout: { indentation: "none", margins: { bottom: "small" } }
        }
      ]
    } : {},
    {
      anchor: "opetuskieli",
      title: __("education.voidaanAntaaMyosSeuraavillaKielilla"),
      layout: { indentation: "none" },
      components: [
        {
          anchor: toissijaisetAnchor,
          name: "Autocomplete",
          short: true,
          properties: {
            forChangeObject: {
              valikko: toissijaisetAnchor
            },
            isPreviewModeOn,
            isReadOnly: _isReadOnly,
            options: toissijaisetOpetuskieletOptions,
            title: __("common.valitseYksiTaiUseampi"),
            value: map(maarays => {
              const maarayksenKoodiarvoUpper = toUpper(maarays.koodiarvo);
              return find(
                propEq("value", maarayksenKoodiarvoUpper),
                toissijaisetOpetuskieletOptions
              );
            }, filter(pathEq(["meta", "valikko"], toissijaisetAnchor), maaraykset))
          }
        }
      ]
    },
    {
      anchor: "opetuskieli",
      components: [
        {
          anchor: "lisatiedot-info",
          name: "StatusTextRow",
          properties: {
            title: __("common.lisatiedotInfo")
          }
        }
      ],
      layout: { margins: { top: "large" } },
      styleClasses: ["mt-10", "pt-10", "border-t"]
    },
    lisatiedotObj
      ? {
          anchor: "lisatiedot",
          components: [
            {
              anchor: lisatiedotObj.koodiarvo,
              name: "TextBox",
              properties: {
                forChangeObject: {
                  koodiarvo: lisatiedotObj.koodiarvo,
                  koodisto: lisatiedotObj.koodisto,
                  versio: lisatiedotObj.versio,
                  voimassaAlkuPvm: lisatiedotObj.voimassaAlkuPvm
                },
                isPreviewModeOn,
                isReadOnly: _isReadOnly,
                title: __("common.lisatiedot"),
                value: lisatietomaarays ? lisatietomaarays.meta.arvo : ""
              }
            }
          ]
        }
      : null
  ].filter(Boolean);
}
